import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { editEntryForm } from '../types';
import SelectField from 'components/select-field';
import { AccountingType } from 'v2-types/order';
import { TFunction } from 'i18next';
import TextField from 'components/text-field';
import DatePicker from 'components/date-picker/date-picker';
import { zodResolver } from '@hookform/resolvers/zod';
import { schemaModalEditEntry } from '../services';
import { isNetProceedsCode } from '../utils/entry-eligibility';
import NumberField from 'components/number-field';

type ModalEditEntryProps = {
  accounting: AccountingType;
  handleClose: () => void;
  t: TFunction;
  kindEntry: 'disbursement' | 'receipt' | 'netfunded' | 'proceeds';
  handleEditEntry: (data: editEntryForm, id: string) => void;
};

const FORM_ID = 'modal-edit-entry-form';

const useStyles = makeStyles((theme: any) => ({
  actions: {
    padding: theme.spacing(1.5, 3, 2),
    gap: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(1.5, 2),
      '& .MuiButton-root': {
        flex: 1,
        minHeight: theme.spacing(5.5),
      },
    },
  },
  actionButton: {
    textTransform: 'none',
  },
}));

export const ModalEditEntry: FC<ModalEditEntryProps> = ({
  handleClose,
  accounting,
  t,
  handleEditEntry,
  kindEntry,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const allowedAssociations = [
    {
      code: 'cash',
      description: t('accounting:cash'),
    },
    {
      code: 'check',
      description: t('accounting:check'),
    },
  ];

  const isNetProceeds = isNetProceedsCode(accounting.code);
  const methods = useForm<editEntryForm>({
    resolver: zodResolver(schemaModalEditEntry()),
    defaultValues: {
      code: isNetProceeds ? undefined : accounting.code,
      description: accounting.description,
      entry_date: accounting.entry_date || undefined,
      amount_override:
        accounting.amount_override === 0 ? accounting.amount : accounting.amount_override,
    },
    mode: 'onChange',
  });
  const {
    handleSubmit,
    control,
    formState: { isValid, errors, isDirty },
    trigger,
    setValue,
  } = methods;

  const handleSubmitForm = handleSubmit((data) => {
    handleEditEntry(data, accounting._id);
    handleClose();
  });

  return (
    <Dialog open fullWidth maxWidth="md" fullScreen={isMobile}>
      <DialogTitle>{t('disbursments:editLinkEntry')}</DialogTitle>
      <DialogContent>
        <form id={FORM_ID} onSubmit={handleSubmitForm}>
          <Grid container spacing={1}>
            {isNetProceeds ? (
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="amount_override"
                  render={({ field: { ref, ...field } }) => (
                    <NumberField
                      {...field}
                      ref={ref}
                      thousandSeparator=","
                      prefix="$"
                      onChange={undefined}
                      value={field.value}
                      onValueChange={(e) => {
                        const amount = e.floatValue || 0;
                        setValue('amount_override', amount, { shouldDirty: true, shouldValidate: true });
                      }}
                      label={t('disbursments:amount_override')}
                      error={!!errors.amount_override}
                      helperText={
                        errors.amount_override?.message
                          ? t(`disbursments:${errors.amount_override?.message}`)
                          : ''
                      }
                    />
                  )}
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="description"
                    render={({ field: { ref, ...field } }) => (
                      <TextField
                        {...field}
                        value={field.value}
                        inputRef={ref}
                        label={t('disbursments:description')}
                        handleBlur={() => {
                          trigger('description');
                        }}
                        error={!!errors.description}
                        helperText={
                          errors.description?.message
                            ? t(`disbursments:${errors.description.message}`)
                            : ''
                        }
                      />
                    )}
                  />
                </Grid>
                {kindEntry !== 'netfunded' && (
                  <Grid item xs={6} sm={3}>
                    <Controller
                      control={control}
                      name="code"
                      render={({ field: { ref, ...field } }) => (
                        <SelectField
                          {...field}
                          value={field.value || 'cash'}
                          inputRef={ref}
                          data={allowedAssociations}
                          dataKey="code"
                          dataValue="code"
                          dataText="description"
                          label={t('users:kind')}
                          handleBlur={() => {
                            trigger('code');
                          }}
                          handleChange={field.onChange}
                        />
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={kindEntry !== 'netfunded' ? 6 : 12} sm={kindEntry !== 'netfunded' ? 3 : 6}>
                  <Controller
                    name="entry_date"
                    control={control}
                    rules={{ required: !isNetProceeds }}
                    render={({ field: { ref, value, ...field } }) => (
                      <DatePicker
                        {...field}
                        inputRef={ref}
                        disableToolbar={false}
                        label={t('accounting:entry-date')}
                        value={value as string | Date}
                        error={!!errors.entry_date}
                        helperText={
                          errors.entry_date?.message
                            ? t(`disbursments:${errors.entry_date.message}`)
                            : ''
                        }
                      />
                    )}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </form>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          color="primary"
          variant="outlined"
          onClick={() => handleClose()}
          disableElevation
          className={classes.actionButton}
        >
          <Typography variant="body2">{t('disbursments:close')}</Typography>
        </Button>
        <Button
          disabled={!isValid || !isDirty}
          color="secondary"
          variant="contained"
          type="submit"
          form={FORM_ID}
          disableElevation
          className={classes.actionButton}
        >
          <Typography variant="body2">{t('disbursments:edit')}</Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalEditEntry;
